/**
 * Choosing your own password, when someone else chose the one you have.
 *
 * Shown instead of the app, not beside it: a temporary password that nobody
 * ever changes is a password the owner knows, written on a sticky note.
 */
import { useState, type FormEvent } from "react";

import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Mark } from "../components/Mark";

export default function ChangePassword() {
  const { session, adopt, signOut } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (next.length < 10) return setError("Choose at least 10 characters.");
    if (next !== again) return setError("The two new passwords don't match.");
    setBusy(true);
    setError(null);
    try {
      const token = await api.auth.changePassword(current, next);
      adopt(token, session?.email ?? "");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change it. Try again.");
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <header className="login-head">
          <Mark size={34} />
          <div>
            <h1>Choose your password</h1>
            <p>{session?.fullName ? `${session.fullName}, you` : "You"}'re signed in with a temporary one.</p>
          </div>
        </header>
        {error && (
          <p className="notice notice-crit" role="alert">
            {error}
          </p>
        )}
        <label className="field">
          <span>The temporary password</span>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" disabled={busy} />
        </label>
        <label className="field">
          <span>New password (10 or more characters)</span>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" disabled={busy} />
        </label>
        <label className="field">
          <span>New password again</span>
          <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" disabled={busy} />
        </label>
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save and continue"}
        </button>
        <p className="login-foot">
          <button type="button" className="link-button" onClick={signOut}>
            Sign out instead
          </button>
        </p>
      </form>
    </main>
  );
}
